/**
 * Phase 27b — Amber warning icon shown on a time_entries row when
 * `break_violation=true && !break_override`. When override is applied,
 * flips to a green check with the note in the tooltip.
 *
 * Purely presentational. Parent (worker week page / approver detail)
 * decides whether to render based on the runtime-engines flag.
 *
 * Props:
 *   entry               — the time_entries doc (must include
 *                         break_violation, break_override, break_override_note,
 *                         raw_minutes, duration_minutes).
 *   onWorkerNote?       — worker-side callback; if provided, icon is
 *                         clickable and opens a note-only sub-dialog.
 *   onApproverOverride? — approver-side callback; when provided AND user
 *                         is an approver, icon becomes a button that opens
 *                         BreakOverrideDialog (parent-managed).
 *   role                — "worker" | "approver" — governs the CTA copy.
 */
import { useState } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";

function minsToHours(m) {
  const n = Number(m || 0);
  return (n / 60).toFixed(2);
}

export default function BreakViolationIcon({
  entry, role = "worker", onApproverOverride, onNoteSaved,
}) {
  const [noteOpen, setNoteOpen] = useState(false);

  if (!entry) return null;

  const isViolation = !!entry.break_violation;
  const isOverridden = !!entry.break_override;

  // Nothing to render — clean entry.
  if (!isViolation && !isOverridden) return null;

  if (isOverridden) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success/15 text-success"
              data-testid={`break-override-badge-${entry.id}`}
              aria-label="Break override applied"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="text-xs font-semibold">Break override applied</div>
            {entry.break_override_note && (
              <div className="mt-1 text-xs italic text-muted-foreground">
                "{entry.break_override_note}"
              </div>
            )}
            <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
              Restored {minsToHours(30)}h from the meal-break rule.
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const rawH = minsToHours(entry.raw_minutes);
  const paidH = minsToHours(entry.duration_minutes);

  const iconEl = (
    <span
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/15 text-amber-700"
      data-testid={`break-violation-icon-${entry.id}`}
      aria-label="Missing meal break"
    >
      <AlertTriangle className="h-3.5 w-3.5" />
    </span>
  );

  if (role === "approver" && onApproverOverride) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onApproverOverride(entry)}
              className="rounded-full transition hover:bg-amber-500/25 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid={`break-violation-btn-${entry.id}`}
              aria-label="Approve break override"
            >
              {iconEl}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="text-xs font-semibold">Missing meal break</div>
            <div className="mt-1 text-xs">
              Raw {rawH}h → Paid {paidH}h due to a rule-based unpaid deduction.
              Click to review & override.
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Worker view: tooltip explains + click opens note-only sub-dialog.
  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="rounded-full transition hover:bg-amber-500/25 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid={`break-violation-btn-${entry.id}`}
              aria-label="Missing meal break — add a note"
            >
              {iconEl}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="text-xs font-semibold">Missing meal break</div>
            <div className="mt-1 text-xs">
              A 30-minute break is required for shifts over 6h. Your paid time
              was reduced by 30 min ({rawH}h → {paidH}h). Add a note if you
              worked through lunch — your approver will review.
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <WorkerBreakNoteDialog
        entry={entry}
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onSaved={() => {
          setNoteOpen(false);
          onNoteSaved && onNoteSaved();
        }}
      />
    </>
  );
}

function WorkerBreakNoteDialog({ entry, open, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      // Best-effort: attach the worker note via the entry patch endpoint.
      // If the field isn't accepted, we surface the error to the worker.
      await api.patch(`/time/entries/${entry.id}`, {
        notes:
          (entry.notes ? entry.notes + "\n" : "") +
          `[worker break note] ${note.trim()}`,
      });
      toast.success("Note attached. Your approver will review.");
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not attach note — the entry may be locked."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="worker-break-note-dialog">
        <DialogHeader>
          <DialogTitle>Add a break note</DialogTitle>
          <DialogDescription>
            Only your approver can override the deduction. Your note gets
            attached to the entry so they have context.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="worker-break-note">What happened?</Label>
          <Textarea
            id="worker-break-note"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Client emergency — worked through my scheduled break."
            data-testid="worker-break-note"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || !note.trim()}
            data-testid="worker-break-note-save"
          >
            Save note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
